import Track from '../models/track'

// Libraries.
import EventEmitter from 'eventemitter3'

/**
 * Manager / store for Track objects.
 * @method TrackManager
 */
export default function TrackManager () {
  /**
   * The Track objects being managed.
   * @type {Map}
   */
  const tracks = new Map()
  const emitter = new EventEmitter()

  /**
   * Retrieve a Track object.
   * @method get
   * @param  {string} trackId
   * @return {Track}
   */
  function get (trackId) {
    return tracks.get(trackId)
  }

  /**
   * Retrieve a list of Track objects.
   * If no tracks are specified, will return all tracks.
   * @method getTracks
   * @param  {Array} [trackIds] List of tracks to retrieve.
   * @return {Array}
   */
  function getTracks (trackIds) {
    if (trackIds) {
      return trackIds.map(trackId => tracks.get(trackId))
    } else {
      return Array.from(tracks.values())
    }
  }

  /**
   * Wraps a MediaStreamTrack into a Track object and adds
   *    it to the manager.
   * @method add
   * @param  {MediaStreamTrack} track A native track object.
   * @param  {MediaStream} stream
   * @param  {boolean} isLocalTrack Specifies if the track parameter is a local one or a remote one.
   * @return {Track} The added/wrapped Track object.
   */
  function add (track, stream, isLocalTrack) {
    const targetTrack = tracks.get(track.id)

    // Chrome issue: track.stream is outdated and needs to be updated to newStream.
    // targetTrack.stream.active is false & targetTrack.stream.getTracks() gives us an empty array.
    // stream.active is true & stream.getTracks() gives us the correct array of tracks.
    // Set/update the new stream as the track's stream
    if (targetTrack && !targetTrack.getStream().active && stream.active) {
      // The track was previously registered and is being re-added with new stream
      targetTrack.setStream(stream)
      return targetTrack
    } else if (targetTrack) {
      // This track is already registered.
      return targetTrack
    } else {
      // Wrap the track as a Track object.
      const wrappedTrack = new Track(track, stream)

      // Mark it as local (or remote) before we save it in the state
      wrappedTrack.setIsLocal(isLocalTrack)

      tracks.set(track.id, wrappedTrack)

      // Remove the track from the manager when it ends.
      wrappedTrack.once('ended', event => {
        if (!event.isUnsolicited) {
          remove({ trackId: track.id })
        }
      })

      emitter.emit('add', wrappedTrack.id)
      return wrappedTrack
    }
  }

  /**
   * Remove a Track from the manager.
   * @method remove
   * @param  {string} trackId
   * @return {Boolean} Whether the Track existed (and hence removed).
   */
  function remove ({ trackId }) {
    const track = get(trackId)
    if (track) {
      tracks.delete(trackId)
      // Clean up any listeners.
      track.off('ended', remove)
      emitter.emit('remove', trackId)
    }

    return Boolean(track)
  }

  function on (...args) {
    return emitter.on(...args)
  }

  function once (...args) {
    return emitter.once(...args)
  }

  function off (...args) {
    return emitter.off(...args)
  }

  /**
   * The exposed API.
   */
  return {
    get,
    getTracks,
    add,
    remove,
    on,
    once,
    off
  }
}
